import { createContext, useCallback, useContext, useMemo } from "react";
import { CommonDataResponse, CommonDataResponseExtended } from "@api/useCommonData";
import { ID } from "@datassential/platform-ui-lib";
import {
    compareRespondentTypeGroups,
    compareRespondentTypes,
    RESPONDENT_TYPE_GROUPS_ORDER,
    RESPONDENT_TYPE_ORDER,
    RespondentType,
    RespondentTypeGroup
} from "@models/RespondentType";
import { IOption } from "@models";
import { ALL_FOOD_BEVERAGE_CATEGORY } from "@core/constants";
import { excludeIndexes, filterItemsByKey, findItemByKey } from "@core/utils/array";
import { compareMetrics, Metric } from "@models/Metric";

export type CommonDataContextType = CommonDataResponseExtended & {
    isLoading: boolean,
};

export const CommonDataContext = createContext<CommonDataContextType>({
    isLoading: true,
});

export function useCommonDataContext() {
    return useContext(CommonDataContext);
}

export function useMetrics(ids: Metric[] = []) {
    const { metrics = [] } = useCommonDataContext();

    return useMemo(
        () => {
            if (ids.length) {
                return metrics
                    .filter((i) => ids.includes(i.id as Metric))
                    .sort((a, b) => compareMetrics(ids, a.id, b.id))
            }

            return metrics;
        },
        [metrics, ids]
    );
}
export function useGetCategories() {
    const { categories } = useCommonDataContext();

    return useCallback(
        (ids: number[]) => {
            return ids.length ? (categories ?? []).filter(i => ids.includes(i.id ?? 0)) : [];
        },
        [categories]
    );
}

export function useCategories(ids: number[]): NonNullable<CommonDataResponse['categories']> {
    const getCategories = useGetCategories();

    return useMemo(
        () => getCategories(ids),
        [getCategories, ids]
    );
}

export function useCategoryOptions(ids: number[]): IOption<number | null, string>[] {
    const categories = useCategories(ids);

    return useMemo(() => [
        { id: null, title: ALL_FOOD_BEVERAGE_CATEGORY },
        ...categories.map((o) => ({
            id: o.id ?? 0,
            title: o.name ?? "",
        })),
    ], [categories]);
}

export function useCategory(id?: number | null) {
    const categories = useCategories(id ? [id] : []);

    return categories[0] ?? null;
}

export function useKeyword(id: ID) {
    const { keywords } = useCommonDataContext();

    return id ? (keywords?.[+id]) : null;
}

export const useKeywords = () => {
    const { keywords } = useCommonDataContext();

    return Object.values(keywords ?? {});
};

export function getMetric(
    metrics: CommonDataResponseExtended['metrics'],
    id: ID,
) {
    return id ? metrics?.find((metric) => metric.id === id) : null;
}

export function useMetric(id: ID) {
    const { metrics } = useCommonDataContext();
    return useMemo(
        () => getMetric(metrics, id),
        [metrics, id]
    );
}

export function useGetMetric() {
    const { metrics } = useCommonDataContext();
    return useCallback(
        (id: ID) => getMetric(metrics, id),
        [metrics]
    );
}

export function useGetRespondentType() {
    const { respondentTypes } = useCommonDataContext();

    return useCallback(
        (id: RespondentType) => id
            ? respondentTypes?.find((type) => type.id === id) ?? null
            : null,
        [respondentTypes]
    );
}

export function useRespondentType(id: RespondentType) {
    const getRespondentType = useGetRespondentType();

    return useMemo(
        () => getRespondentType(id),
        [id, getRespondentType]
    );
}

export function useRespondentTypesGroups({
    isEatingHabitsExtended = false,
}: {
    isEatingHabitsExtended?: boolean;
} = {}) {
    const {
        respondentTypes,
        respondentTypesGroups: respondentTypesGroupsRaw,
    } = useCommonDataContext();

    const respondentTypesGroupsExtended = useMemo(
        () => {
            const initialGroups = (respondentTypesGroupsRaw ?? []);
            const eatingHabits = initialGroups.find((group) => group.id === RespondentTypeGroup.EatingHabits);
            const eatingHabitsIndex = initialGroups.findIndex((group) => group.id === RespondentTypeGroup.EatingHabits);
            const foodies = initialGroups.find((group) => group.id === RespondentTypeGroup.FoodiesGroup);
            const foodiesIndex = initialGroups.findIndex((group) => group.id === RespondentTypeGroup.FoodiesGroup);
            const healthFocused = initialGroups.find((group) => group.id === RespondentTypeGroup.HealthFocusedGroup);
            const healthFocusedIndex = initialGroups.findIndex((group) => group.id === RespondentTypeGroup.HealthFocusedGroup);

            return [
                ...excludeIndexes(initialGroups, [eatingHabitsIndex, foodiesIndex, healthFocusedIndex]),
                {
                    ...eatingHabits,
                    respondentTypes: [
                        ...(eatingHabits?.respondentTypes ?? []),
                        ...(foodies?.respondentTypes ?? []),
                        ...(healthFocused?.respondentTypes ?? []),
                    ],
                },
            ];
        },
        [respondentTypesGroupsRaw]
    );

    const respondentTypesGroups = useMemo(
        () => {
            if (isEatingHabitsExtended) {
                return respondentTypesGroupsExtended;
            }

            return respondentTypesGroupsRaw;
        },
        [isEatingHabitsExtended, respondentTypesGroupsRaw, respondentTypesGroupsExtended]
    );

    return useMemo(
        () => {
            return [...respondentTypesGroups ?? []]
                .sort((a, b) => {
                    return compareRespondentTypeGroups(RESPONDENT_TYPE_GROUPS_ORDER, a.id, b.id);
                })
                .map((group) => ({
                    ...group,
                    items: (respondentTypes ?? [])
                        .filter((o) => group.respondentTypes?.includes(o.id!))
                        .sort((a, b) => {
                            return compareRespondentTypes(RESPONDENT_TYPE_ORDER, a.id, b.id);
                        }),
                }));
        },
        [respondentTypes, respondentTypesGroups]
    );
}

export function useRespondentTypes(ids?: RespondentType[]) {
    const { respondentTypes = [] } = useCommonDataContext();

    return filterItemsByKey(respondentTypes, ids as typeof respondentTypes[number]['id'][]);
}

export function useRestaurants(ids?: number[]) {
    const { restaurants = [] } = useCommonDataContext();

    return filterItemsByKey(restaurants, ids);
}

export function useCStore(ids?: number[]) {
    const { cStores = [] } = useCommonDataContext();

    return filterItemsByKey(cStores, ids);
}

export function useSupermarkets(ids?: number[]) {
    const { supermarkets = [] } = useCommonDataContext();

    return filterItemsByKey(supermarkets, ids);
}

export function useHotels(ids?: number[]) {
    const { hotels = [] } = useCommonDataContext();

    return filterItemsByKey(hotels, ids);
}

export function useGetChains() {
    const { restaurants = [], cStores = [], supermarkets = [], hotels = [] } = useCommonDataContext();

    return useCallback(
        (ids: number[]) => {
            return filterItemsByKey(
                [
                    ...restaurants,
                    ...cStores,
                    ...supermarkets,
                    ...hotels,
                ],
                ids
            );
        },
        [restaurants, cStores, supermarkets, hotels]
    );
}

export function useGetChain() {
    const getChains = useGetChains();

    return useCallback(
        (id: number) => {
            return getChains([id])[0] ?? null;
        },
        [getChains]
    );
}

export function useBrands(ids?: number[]) {
    const { cpgBrands = [] } = useCommonDataContext();

    return filterItemsByKey(cpgBrands, ids);
}

export function getBrand(
    brands: NonNullable<CommonDataResponseExtended['cpgBrands']>,
    id: number,
) {
    return findItemByKey(brands, id) ?? null;
}

export function useGetBrand() {
    const { cpgBrands } = useCommonDataContext();

    return useCallback(
        (id: number) => getBrand(cpgBrands ?? [], id),
        [cpgBrands]
    );
}

export function useDeliveryServices(ids?: number[]) {
    const { deliveryServices = [] } = useCommonDataContext();

    return filterItemsByKey(deliveryServices, ids as typeof deliveryServices[number]['id'][]);
}

export function getDeliveryService(
    deliveryServices: NonNullable<CommonDataResponseExtended['deliveryServices']>,
    id: number,
) {
    return findItemByKey(deliveryServices, id) ?? null;
}

export function useGetDeliveryService() {
    const { deliveryServices } = useCommonDataContext();

    return useCallback(
        (id: number) => getDeliveryService(deliveryServices ?? [], id),
        [deliveryServices]
    );
}

export function useSegments(ids?: number[]) {
    const { segments = [] } = useCommonDataContext();

    return filterItemsByKey(segments, ids);
}

export function getSegment(
    segments: NonNullable<CommonDataResponseExtended['segments']>,
    id: number,
) {
    return findItemByKey(segments, id) ?? null;
}

export function useGetSegment() {
    const { segments } = useCommonDataContext();

    return useCallback(
        (id: number) => getSegment(segments ?? [], id),
        [segments]
    );
}

export function useOnSites(ids?: number[]) {
    const { onSites = [] } = useCommonDataContext();

    return filterItemsByKey(onSites, ids as typeof onSites[number]['id'][]);
}

export function getOnSite(
    onSites: NonNullable<CommonDataResponseExtended['onSites']>,
    id: number,
) {
    return findItemByKey(onSites, id) ?? null;
}

export function useGetOnSite() {
    const { onSites } = useCommonDataContext();

    return useCallback(
        (id: number) => getOnSite(onSites ?? [], id),
        [onSites]
    );
}

export function useSegmentsOnSitesWithChildren() {
    const restaurants = useRestaurants();
    const supermarkets = useSupermarkets();
    const cStores = useCStore();
    const hotels = useHotels();
    const onSites = useOnSites();
    const segments = useSegments();

    return [...segments, ...onSites]
        .map((i) => ({
            ...i,
            items: [
                ...restaurants,
                ...supermarkets,
                ...cStores,
                ...hotels,
            ].filter(j => j.segmentId === i.id),
        }));
}

export function useGetMacStage() {
    const { macStages } = useCommonDataContext();

    return useCallback(
        (id: ID) => {
            return id ? macStages?.find((stage) => stage.id === id) ?? null : null;
        },
        [macStages]
    );
}

export function useGetHeatScore() {
    const { heatScores } = useCommonDataContext();

    return useCallback(
        (id: ID) => {
            return id ? heatScores?.find((score) => score.id === id) ?? null : null;
        },
        [heatScores]
    );
}